import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  MenuItem,
  Autocomplete,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  Alert,
  Divider,
  InputAdornment,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as BackIcon,
  AttachFile as AttachIcon,
  Calculate as CalculateIcon
} from '@mui/icons-material';
import axios from 'axios';
import Swal from 'sweetalert2';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getAllActiveEmployeesAPICall } from '../../../../API/Staff/HRM/employeeAPI';
import { getAllDepartmentAPICall, getAllDesignationAPICall } from '../../../../API/Staff/HRM/departmentAndDesignationAPI';
import { viewAllBranchesAPICall } from '../../../../API/Settings/Company Settings/companySettingsAPI';

const TransitionCreateForm = () => {
  const navigate = useNavigate();
  const { id } = useParams(); // Get ID from URL for edit mode
  const isEditMode = Boolean(id);
  
  const userMeta = localStorage.getItem('login_meta');
  const { companyCode, branchId } = userMeta ? JSON.parse(userMeta) : {};

  const [activeStep, setActiveStep] = useState(0);
  const [employees, setEmployees] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingTransition, setLoadingTransition] = useState(false);

  // Global HR data from Redux
  const allActiveEmpList = useSelector(state => state.allActiveEmployeeSlice.value);
  const allDesignationList = useSelector(state => state.allDesignationListSlice.value);
  const allDepartmentList = useSelector(state => state.allDepartmentSlice.value);
  const allBranchesList = useSelector(state => state.allBranchesSlice.value);

  const transitionTypes = [
    { value: 'PROMOTION', label: 'Promotion' },
    { value: 'TRANSFER', label: 'Transfer' },
    { value: 'INCREMENT', label: 'Increment' },
    { value: 'DEMOTION', label: 'Demotion' },
    { value: 'DESIGNATION_CHANGE', label: 'Designation Change' },
    { value: 'DEPARTMENT_CHANGE', label: 'Department Change' },
    { value: 'RESIGNATION', label: 'Resignation' },
    { value: 'TERMINATION', label: 'Termination' },
    { value: 'JOB_OFFER', label: 'Job Offer' }
  ];

  const [formData, setFormData] = useState({
    employeeId: '',
    transitionType: 'INCREMENT',
    effectiveDate: '',
    
    // Previous details
    previousDesignation: '',
    previousDepartment: '',
    previousBranch: '',
    previousSalary: 0,
    previousGrade: '',
    
    // New details
    newDesignation: '',
    newDepartment: '',
    newBranch: '',
    newSalary: 0,
    newGrade: '',
    
    // Increment details
    incrementAmount: 0,
    incrementPercentage: 0,
    incrementReason: '',
    
    // Resignation details
    resignation: {
      reason: '',
      noticePeriodDays: 30,
      lastWorkingDate: '',
      exitInterviewNotes: '',
      isNoticePeriodWaived: false
    },

    // Job Offer details
    jobOffer: {
      candidateName: '',
      candidateEmail: '',
      candidatePhone: '', // Added
      jobTitle: '',
      offeredSalary: 0,
      joiningDate: '',
      expiryDate: '',
      offerLetterUrl: ''
    },
    
    // Additional
    notes: '',
    documents: []
  });

  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [calculationMode, setCalculationMode] = useState('amount'); // 'amount' or 'percentage'

  // Predefined grade options (fallback if designation has no grades)
  const defaultGradeOptions = [
    'Grade A', 'Grade B', 'Grade C', 'Grade D', 'Grade E',
    'Level 1', 'Level 2', 'Level 3', 'Level 4', 'Level 5',
    'Junior', 'Mid-Level', 'Senior', 'Lead', 'Principal',
    'Entry', 'Intermediate', 'Advanced', 'Expert'
  ];

  // Get grades from selected designation or use default (memoized to avoid recalculation)
  const gradeOptions = useMemo(() => {
    if (formData.newDesignation) {
      const selectedDesignation = designations.find(d => d._id === formData.newDesignation);
      if (selectedDesignation?.grades && selectedDesignation.grades.length > 0) {
        return selectedDesignation.grades.map(g => g.name);
      }
    }
    return defaultGradeOptions;
  }, [formData.newDesignation, designations]);

  const steps = ['Select Employee & Type', 'Previous Details', 'New Details', 'Review & Submit'];

  // Fetch data using existing HR APIs (populates Redux slices)
  useEffect(() => {
    getAllActiveEmployeesAPICall();
    getAllDesignationAPICall();
    getAllDepartmentAPICall();
    viewAllBranchesAPICall();
  }, []);

  // Load transition data in edit mode
  useEffect(() => {
    if (isEditMode && id) {
      loadTransitionData();
    }
  }, [isEditMode, id]);

  const loadTransitionData = async () => {
    try {
      setLoadingTransition(true);
      const response = await axios.get(`/api/employee-transition/${id}`);
      
      if (response.data.success) {
        const transition = response.data.data;
        
        // Format date for input field (YYYY-MM-DD)
        const formattedDate = transition.effectiveDate 
          ? new Date(transition.effectiveDate).toISOString().split('T')[0] 
          : '';
        
        // Populate form with transition data
        setFormData({
          employeeId: transition.employeeId?._id || transition.employeeId || '',
          transitionType: transition.transitionType || 'INCREMENT',
          effectiveDate: formattedDate,
          
          previousDesignation: transition.previousDesignation?._id || transition.previousDesignation || '',
          previousDepartment: transition.previousDepartment?._id || transition.previousDepartment || '',
          previousBranch: transition.previousBranch?._id || transition.previousBranch || '',
          previousSalary: transition.previousSalary || 0,
          previousGrade: transition.previousGrade || '',
          
          newDesignation: transition.newDesignation?._id || transition.newDesignation || '',
          newDepartment: transition.newDepartment?._id || transition.newDepartment || '',
          newBranch: transition.newBranch?._id || transition.newBranch || '',
          newSalary: transition.newSalary || 0,
          newGrade: transition.newGrade || '',
          
          incrementAmount: transition.incrementAmount || 0,
          incrementPercentage: transition.incrementPercentage || 0,
          incrementReason: transition.incrementReason || '',
          
          // Resignation details (merge with defaults)
          resignation: {
            reason: transition.resignation?.reason || '',
            noticePeriodDays: transition.resignation?.noticePeriodDays || 30,
            lastWorkingDate: transition.resignation?.lastWorkingDate || '',
            exitInterviewNotes: transition.resignation?.exitInterviewNotes || '',
            isNoticePeriodWaived: transition.resignation?.isNoticePeriodWaived || false
          },

          // Job Offer details (merge with defaults)
          jobOffer: {
            candidateName: transition.jobOffer?.candidateName || '',
            candidateEmail: transition.jobOffer?.candidateEmail || '',
            candidatePhone: transition.jobOffer?.candidatePhone || '', // Added
            jobTitle: transition.jobOffer?.jobTitle || '',
            offeredSalary: transition.jobOffer?.offeredSalary || 0,
            joiningDate: transition.jobOffer?.joiningDate || '',
            expiryDate: transition.jobOffer?.expiryDate || '',
            offerLetterUrl: transition.jobOffer?.offerLetterUrl || ''
          },

          notes: transition.notes || '',
          documents: transition.documents || []
        });

        // Set selected employee for display
        if (transition.employeeId) {
          setSelectedEmployee(transition.employeeId);
        }
      }
    } catch (error) {
      console.error('Error loading transition:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to load transition data'
      });
      navigate('/userdashboard/hr/employee-transition');
    } finally {
      setLoadingTransition(false);
    }
  };

  // Sync local component state from Redux slices
  useEffect(() => {
    setEmployees(allActiveEmpList || []);
  }, [allActiveEmpList]);

  useEffect(() => {
    setDesignations(allDesignationList || []);
  }, [allDesignationList]);

  useEffect(() => {
    setDepartments(allDepartmentList || []);
  }, [allDepartmentList]);

  useEffect(() => {
    setBranches(allBranchesList || []);
  }, [allBranchesList]);

  // Handle employee selection
  const handleEmployeeSelect = (employee) => {
    if (employee) {
      setSelectedEmployee(employee);
      setFormData(prev => ({
        ...prev,
        employeeId: employee._id,
        previousDesignation: employee.designation || '',
        previousDepartment: employee.department || '',
        // Use outletLocation (ObjectId) instead of branchId (string code)
        previousBranch: employee.outletLocation || branchId,
        previousSalary: employee.monthlySalary || employee.basic_salary || employee.salary || 0,
        previousGrade: employee.grade || ''
      }));
    }
  };

  // Calculate increment
  const calculateIncrement = () => {
    if (calculationMode === 'amount') {
      // Calculate percentage from amount
      const percentage = formData.previousSalary > 0 
        ? (formData.incrementAmount / formData.previousSalary) * 100 
        : 0;
      const newSalary = formData.previousSalary + formData.incrementAmount;
      
      setFormData(prev => ({
        ...prev,
        incrementPercentage: Math.round(percentage * 100) / 100,
        newSalary
      }));
    } else {
      // Calculate amount from percentage
      const amount = (formData.previousSalary * formData.incrementPercentage) / 100;
      const newSalary = formData.previousSalary + amount;
      
      setFormData(prev => ({
        ...prev,
        incrementAmount: Math.round(amount * 100) / 100,
        newSalary
      }));
    }
  };

  // Auto-calculate when values change
  useEffect(() => {
    if (formData.previousSalary > 0 && (formData.incrementAmount > 0 || formData.incrementPercentage > 0)) {
      calculateIncrement();
    }
  }, [formData.incrementAmount, formData.incrementPercentage, calculationMode]);

  // Handle form submission
  const handleSubmit = async () => {
    // Validation
    if (formData.transitionType !== 'JOB_OFFER' && !formData.employeeId) {
      Swal.fire('Error', 'Please select an employee', 'error');
      return;
    }

    if (!formData.effectiveDate) {
      Swal.fire('Error', 'Please select effective date', 'error');
      return;
    }

    // Type-specific validation
    if (['PROMOTION', 'DESIGNATION_CHANGE'].includes(formData.transitionType)) {
      if (!formData.newDesignation) {
        Swal.fire('Error', 'Please select new designation', 'error');
        return;
      }
    }

    if (formData.transitionType === 'INCREMENT') {
      if (formData.incrementAmount <= 0) {
        Swal.fire('Error', 'Increment amount must be greater than 0', 'error');
        return;
      }
    }

    if (formData.transitionType === 'JOB_OFFER') {
      const { candidateName, candidateEmail, candidatePhone, jobTitle, offeredSalary, joiningDate, expiryDate } = formData.jobOffer;
      if (!candidateName || !candidateEmail || !candidatePhone || !jobTitle || !offeredSalary || !joiningDate || !expiryDate) {
        Swal.fire('Error', 'Please fill all required job offer details', 'error');
        return;
      }
      if (!formData.newDepartment) {
        Swal.fire('Error', 'Please select a Department', 'error');
        return;
      }
    }

    if (['RESIGNATION', 'TERMINATION'].includes(formData.transitionType)) {
      const { reason, lastWorkingDate } = formData.resignation;
      if (!reason || !lastWorkingDate) {
        Swal.fire('Error', 'Reason and Last Working Date are required', 'error');
        return;
      }
    }

    setLoading(true);

    try {
      // Clean up the data - remove empty strings and invalid ObjectIds
      const cleanedData = { ...formData };
      
      // Remove empty string fields that should be ObjectIds
      Object.keys(cleanedData).forEach(key => {
        if (cleanedData[key] === '' && 
            (key === 'employeeId' || key.includes('Designation') || key.includes('Department') || key.includes('Branch'))) {
          delete cleanedData[key];
        }
      });

      // Use branchId from token/localStorage instead of employee.branchId string
      if (branchId) {
        cleanedData.branchId = branchId;
        if (!cleanedData.previousBranch) {
          cleanedData.previousBranch = branchId;
        }
      }

      // Use PUT for edit, POST for create
      const response = isEditMode
        ? await axios.put(`/api/employee-transition/${id}`, cleanedData)
        : await axios.post('/api/employee-transition/create', cleanedData);

      if (response.data.success) {
        Swal.fire({
          title: 'Success!',
          text: isEditMode 
            ? 'Transition updated successfully' 
            : 'Transition request created successfully',
          icon: 'success',
          confirmButtonText: 'OK'
        }).then(() => {
          navigate('/userdashboard/hr/employee-transition');
        });
      }
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} transition:`, error);
      Swal.fire('Error', error.response?.data?.message || `Failed to ${isEditMode ? 'update' : 'create'} transition`, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Handle next step
  const handleNext = () => {
    // Validation for each step
    if (activeStep === 0) {
      if (formData.transitionType !== 'JOB_OFFER' && !formData.employeeId) {
        Swal.fire('Error', 'Please select an employee', 'error');
        return;
      }
      if (!formData.effectiveDate) {
        Swal.fire('Error', 'Please select effective date', 'error');
        return;
      }
    }

    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  // Render step content
  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box sx={{ maxWidth: 900, mx: 'auto' }}>
            <Grid container spacing={3}>
              
              {/* Transition Type Selection - Moved to top */}
              <Grid item xs={12}>
                <Card 
                  elevation={0}
                  sx={{ 
                    border: '1px solid #e0e0e0',
                    borderRadius: 2
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        mb: 2.5, 
                        fontWeight: 600,
                        color: '#1976d2'
                      }}
                    >
                      Transition Details
                    </Typography>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          select
                          fullWidth
                          label="Transition Type"
                          value={formData.transitionType}
                          onChange={(e) => setFormData(prev => ({ ...prev, transitionType: e.target.value }))}
                          required
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '&:hover fieldset': {
                                borderColor: '#1976d2',
                              },
                            },
                          }}
                        >
                          {transitionTypes.map((type) => (
                            <MenuItem key={type.value} value={type.value}>
                              {type.label}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          type="date"
                          label="Effective Date"
                          value={formData.effectiveDate}
                          onChange={(e) => setFormData(prev => ({ ...prev, effectiveDate: e.target.value }))}
                          InputLabelProps={{ shrink: true }}
                          inputProps={{ min: new Date().toISOString().split('T')[0] }}
                          required
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '&:hover fieldset': {
                                borderColor: '#1976d2',
                              },
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Employee Selection Card (Hidden for Job Offer) */}
              {formData.transitionType !== 'JOB_OFFER' && (
                <Grid item xs={12}>
                  <Card 
                    elevation={0} 
                    sx={{ 
                      border: '1px solid #e0e0e0',
                      borderRadius: 2,
                      overflow: 'visible'
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          mb: 2.5, 
                          fontWeight: 600,
                          color: '#1976d2'
                        }}
                      >
                        Select Employee
                      </Typography>
                      <Autocomplete
                        options={employees}
                        getOptionLabel={(option) =>
                          option
                            ? `${option.staff_name || ''} (${option.emp_id || ''})`
                            : ''
                        }
                        value={selectedEmployee}
                        onChange={(e, value) => handleEmployeeSelect(value)}
                        renderInput={(params) => (
                          <TextField 
                            {...params} 
                            label="Search Employee" 
                            placeholder="Type employee name or ID..."
                            required
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                '&:hover fieldset': {
                                  borderColor: '#1976d2',
                                },
                              },
                            }}
                          />
                        )}
                      />
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {/* Current Employee Details Card */}
              {selectedEmployee && formData.transitionType !== 'JOB_OFFER' && (
                <Grid item xs={12}>
                  <Card 
                    elevation={0}
                    sx={{ 
                      border: '1px solid #e0e0e0',
                      borderRadius: 2,
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white'
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          mb: 2.5, 
                          fontWeight: 600,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1
                        }}
                      >
                        <Box 
                          sx={{ 
                            width: 8, 
                            height: 8, 
                            borderRadius: '50%', 
                            bgcolor: '#4caf50' 
                          }} 
                        />
                        Current Employee Details
                      </Typography>
                      <Grid container spacing={3}>
                        <Grid item xs={12} sm={6} md={3}>
                          <Box>
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                opacity: 0.8,
                                textTransform: 'uppercase',
                                letterSpacing: 0.5,
                                fontSize: '0.7rem'
                              }}
                            >
                              Designation
                            </Typography>
                            <Typography 
                              variant="body1" 
                              sx={{ 
                                fontWeight: 600,
                                mt: 0.5,
                                fontSize: '1rem'
                              }}
                            >
                              {selectedEmployee.designationName || 'N/A'}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <Box>
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                opacity: 0.8,
                                textTransform: 'uppercase',
                                letterSpacing: 0.5,
                                fontSize: '0.7rem'
                              }}
                            >
                              Department
                            </Typography>
                            <Typography 
                              variant="body1" 
                              sx={{ 
                                fontWeight: 600,
                                mt: 0.5,
                                fontSize: '1rem'
                              }}
                            >
                              {selectedEmployee.departmentName || 'N/A'}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <Box>
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                opacity: 0.8,
                                textTransform: 'uppercase',
                                letterSpacing: 0.5,
                                fontSize: '0.7rem'
                              }}
                            >
                              Current Salary
                            </Typography>
                            <Typography 
                              variant="body1" 
                              sx={{ 
                                fontWeight: 600,
                                mt: 0.5,
                                fontSize: '1rem'
                              }}
                            >
                              ₹{(selectedEmployee.monthlySalary || selectedEmployee.basic_salary || selectedEmployee.salary || 0).toLocaleString()}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <Box>
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                opacity: 0.8,
                                textTransform: 'uppercase',
                                letterSpacing: 0.5,
                                fontSize: '0.7rem'
                              }}
                            >
                              Grade
                            </Typography>
                            <Typography 
                              variant="body1" 
                              sx={{ 
                                fontWeight: 600,
                                mt: 0.5,
                                fontSize: '1rem'
                              }}
                            >
                              {selectedEmployee.grade || 'N/A'}
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>
          </Box>
        );

      case 1:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Previous Details
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            {['PROMOTION', 'DESIGNATION_CHANGE', 'DEMOTION'].includes(formData.transitionType) && (
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={designations}
                  getOptionLabel={(option) => option.position || option.name || ''}
                  value={designations.find(d => d._id === formData.previousDesignation) || null}
                  onChange={(e, value) => setFormData(prev => ({ ...prev, previousDesignation: value?._id || '' }))}
                  renderInput={(params) => (
                    <TextField {...params} label="Previous Designation" />
                  )}
                  disabled
                />
              </Grid>
            )}

            {['TRANSFER', 'DEPARTMENT_CHANGE'].includes(formData.transitionType) && (
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={departments}
                  getOptionLabel={(option) => option.departmentName || option.name || ''}
                  value={departments.find(d => d._id === formData.previousDepartment) || null}
                  onChange={(e, value) => setFormData(prev => ({ ...prev, previousDepartment: value?._id || '' }))}
                  renderInput={(params) => (
                    <TextField {...params} label="Previous Department" />
                  )}
                  disabled
                />
              </Grid>
            )}

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Previous Salary"
                value={formData.previousSalary}
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
                disabled
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Previous Grade"
                value={formData.previousGrade}
                disabled
              />
            </Grid>
          </Grid>
        );

      case 2:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                {formData.transitionType === 'RESIGNATION' ? 'Resignation Details' : 
                 formData.transitionType === 'TERMINATION' ? 'Termination Details' :
                 formData.transitionType === 'JOB_OFFER' ? 'Job Offer Details' :
                 'New Details'}
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            {/* JOB OFFER FORM */}
            {formData.transitionType === 'JOB_OFFER' && (
              <>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Candidate Name"
                    value={formData.jobOffer.candidateName}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      jobOffer: { ...prev.jobOffer, candidateName: e.target.value } 
                    }))}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Candidate Email"
                    type="email"
                    value={formData.jobOffer.candidateEmail}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      jobOffer: { ...prev.jobOffer, candidateEmail: e.target.value } 
                    }))}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Candidate Phone"
                    value={formData.jobOffer.candidatePhone}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      jobOffer: { ...prev.jobOffer, candidatePhone: e.target.value } 
                    }))}
                    required
                    placeholder="+91 1234567890"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    options={departments}
                    getOptionLabel={(option) => option.departmentName || option.name || ''}
                    value={departments.find(d => d._id === formData.newDepartment) || null}
                    onChange={(e, value) => setFormData(prev => ({ ...prev, newDepartment: value?._id || '' }))}
                    renderInput={(params) => (
                      <TextField 
                        {...params} 
                        label="Department" 
                        required 
                        error={!formData.newDepartment}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    freeSolo
                    options={designations.filter(d => !formData.newDepartment || d.departmentId === formData.newDepartment)}
                    getOptionLabel={(option) => {
                      if (typeof option === 'string') return option;
                      return option.position || option.name || '';
                    }}
                    value={
                      formData.newDesignation 
                        ? designations.find(d => d._id === formData.newDesignation) 
                        : formData.jobOffer.jobTitle
                    }
                    onChange={(e, value) => {
                      if (typeof value === 'string') {
                        // User typed a new value
                        setFormData(prev => ({
                          ...prev,
                          newDesignation: '', // New designation ID is empty
                          jobOffer: { ...prev.jobOffer, jobTitle: value }
                        }));
                      } else if (value && typeof value === 'object') {
                        // User selected existing designation
                        setFormData(prev => ({
                          ...prev,
                          newDesignation: value._id,
                          jobOffer: { ...prev.jobOffer, jobTitle: value.position }
                        }));
                      } else {
                        // Cleared
                        setFormData(prev => ({
                          ...prev,
                          newDesignation: '',
                          jobOffer: { ...prev.jobOffer, jobTitle: '' }
                        }));
                      }
                    }}
                    renderInput={(params) => (
                      <TextField 
                        {...params} 
                        label="Job Title / Designation" 
                        required 
                        helperText={!formData.newDepartment ? "Select Department first" : "Select existing or type new"}
                      />
                    )}
                    disabled={!formData.newDepartment}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Offered Salary"
                    value={formData.jobOffer.offeredSalary}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      jobOffer: { ...prev.jobOffer, offeredSalary: parseFloat(e.target.value) || 0 } 
                    }))}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                    }}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Joining Date"
                    value={formData.jobOffer.joiningDate ? new Date(formData.jobOffer.joiningDate).toISOString().split('T')[0] : ''}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      jobOffer: { ...prev.jobOffer, joiningDate: e.target.value } 
                    }))}
                    InputLabelProps={{ shrink: true }}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Offer Expiry Date"
                    value={formData.jobOffer.expiryDate ? new Date(formData.jobOffer.expiryDate).toISOString().split('T')[0] : ''}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      jobOffer: { ...prev.jobOffer, expiryDate: e.target.value } 
                    }))}
                    InputLabelProps={{ shrink: true }}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Offer Letter URL (Optional)"
                    value={formData.jobOffer.offerLetterUrl}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      jobOffer: { ...prev.jobOffer, offerLetterUrl: e.target.value } 
                    }))}
                    placeholder="https://..."
                  />
                </Grid>
              </>
            )}

            {/* RESIGNATION / TERMINATION FORM */}
            {['RESIGNATION', 'TERMINATION'].includes(formData.transitionType) && (
              <>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Reason"
                    value={formData.resignation.reason}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      resignation: { ...prev.resignation, reason: e.target.value } 
                    }))}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Notice Period (Days)"
                    value={formData.resignation.noticePeriodDays}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      resignation: { ...prev.resignation, noticePeriodDays: parseInt(e.target.value) || 0 } 
                    }))}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Last Working Date"
                    value={formData.resignation.lastWorkingDate ? new Date(formData.resignation.lastWorkingDate).toISOString().split('T')[0] : ''}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      resignation: { ...prev.resignation, lastWorkingDate: e.target.value } 
                    }))}
                    InputLabelProps={{ shrink: true }}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.resignation.isNoticePeriodWaived}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          resignation: { ...prev.resignation, isNoticePeriodWaived: e.target.checked } 
                        }))}
                      />
                    }
                    label="Waive Notice Period"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Exit Interview Notes"
                    value={formData.resignation.exitInterviewNotes}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      resignation: { ...prev.resignation, exitInterviewNotes: e.target.value } 
                    }))}
                  />
                </Grid>
              </>
            )}

            {/* STANDARD FORMS (Promotion, Transfer, etc.) */}
            {['PROMOTION', 'DESIGNATION_CHANGE', 'DEMOTION'].includes(formData.transitionType) && (
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={designations}
                  getOptionLabel={(option) => option.position || option.name || ''}
                  value={designations.find(d => d._id === formData.newDesignation) || null}
                  onChange={(e, value) => setFormData(prev => ({ ...prev, newDesignation: value?._id || '' }))}
                  renderInput={(params) => (
                    <TextField {...params} label="New Designation *" required />
                  )}
                />
              </Grid>
            )}

            {['TRANSFER', 'DEPARTMENT_CHANGE'].includes(formData.transitionType) && (
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={departments}
                  getOptionLabel={(option) => option.departmentName || option.name || ''}
                  value={departments.find(d => d._id === formData.newDepartment) || null}
                  onChange={(e, value) => setFormData(prev => ({ ...prev, newDepartment: value?._id || '' }))}
                  renderInput={(params) => (
                    <TextField {...params} label="New Department *" required />
                  )}
                />
              </Grid>
            )}

            {formData.transitionType === 'TRANSFER' && (
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={branches}
                  getOptionLabel={(option) => option.branchName || option.name || ''}
                  value={branches.find(b => b._id === formData.newBranch) || null}
                  onChange={(e, value) => setFormData(prev => ({ ...prev, newBranch: value?._id || '' }))}
                  renderInput={(params) => (
                    <TextField {...params} label="New Branch" />
                  )}
                />
              </Grid>
            )}

            {formData.transitionType === 'INCREMENT' && (
              <>
                <Grid item xs={12}>
                  <Alert severity="info">
                    Enter either increment amount or percentage. The other will be calculated automatically.
                  </Alert>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Increment Amount"
                    value={formData.incrementAmount}
                    onChange={(e) => {
                      setCalculationMode('amount');
                      setFormData(prev => ({ ...prev, incrementAmount: parseFloat(e.target.value) || 0 }));
                    }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                      endAdornment: (
                        <InputAdornment position="end">
                          <CalculateIcon color="action" />
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Increment Percentage"
                    value={formData.incrementPercentage}
                    onChange={(e) => {
                      setCalculationMode('percentage');
                      setFormData(prev => ({ ...prev, incrementPercentage: parseFloat(e.target.value) || 0 }));
                    }}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">%</InputAdornment>
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="New Salary"
                    value={formData.newSalary}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                    }}
                    disabled
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Increment Reason *"
                    value={formData.incrementReason}
                    onChange={(e) => setFormData(prev => ({ ...prev, incrementReason: e.target.value }))}
                    placeholder="E.g., Annual performance increment, Promotion increment, etc."
                  />
                </Grid>
              </>
            )}

            {['PROMOTION', 'DESIGNATION_CHANGE'].includes(formData.transitionType) && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="New Salary"
                  value={formData.newSalary}
                  onChange={(e) => setFormData(prev => ({ ...prev, newSalary: parseFloat(e.target.value) || 0 }))}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                  }}
                />
              </Grid>
            )}

            {/* Grade Field for relevant types (exclude Resignation/Termination) */}
            {!['RESIGNATION', 'TERMINATION', 'JOB_OFFER'].includes(formData.transitionType) && (
              <Grid item xs={12} md={6}>
                <Autocomplete
                  fullWidth
                  freeSolo
                  options={gradeOptions}
                  value={formData.newGrade}
                  onChange={(e, newValue) => setFormData(prev => ({ ...prev, newGrade: newValue || '' }))}
                  onInputChange={(e, newInputValue) => setFormData(prev => ({ ...prev, newGrade: newInputValue || '' }))}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="New Grade"
                      helperText="Select from list or type custom grade"
                    />
                  )}
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Notes"
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Additional notes or comments about this transition..."
              />
            </Grid>
          </Grid>
        );

      case 3:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Review & Confirm
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="subtitle1" color="primary" gutterBottom>
                        Employee Information
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="textSecondary">Employee</Typography>
                      <Typography variant="body1">{selectedEmployee?.name}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="textSecondary">Employee Code</Typography>
                      <Typography variant="body1">{selectedEmployee?.employeeCode}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="textSecondary">Transition Type</Typography>
                      <Typography variant="body1">
                        {transitionTypes.find(t => t.value === formData.transitionType)?.label}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="textSecondary">Effective Date</Typography>
                      <Typography variant="body1">
                        {new Date(formData.effectiveDate).toLocaleDateString()}
                      </Typography>
                    </Grid>

                    {formData.transitionType === 'INCREMENT' && (
                      <>
                        <Grid item xs={12}>
                          <Divider sx={{ my: 1 }} />
                          <Typography variant="subtitle1" color="primary" gutterBottom>
                            Increment Details
                          </Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="body2" color="textSecondary">Previous Salary</Typography>
                          <Typography variant="h6">₹{formData.previousSalary.toLocaleString()}</Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="body2" color="textSecondary">Increment</Typography>
                          <Typography variant="h6" color="success.main">
                            +₹{formData.incrementAmount.toLocaleString()} ({formData.incrementPercentage}%)
                          </Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="body2" color="textSecondary">New Salary</Typography>
                          <Typography variant="h6" color="primary">
                            ₹{formData.newSalary.toLocaleString()}
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="body2" color="textSecondary">Reason</Typography>
                          <Typography variant="body1">{formData.incrementReason}</Typography>
                        </Grid>
                      </>
                    )}

                    {formData.notes && (
                      <>
                        <Grid item xs={12}>
                          <Divider sx={{ my: 1 }} />
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="body2" color="textSecondary">Notes</Typography>
                          <Typography variant="body1">{formData.notes}</Typography>
                        </Grid>
                      </>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Alert severity="warning">
                Once submitted, this transition request will be sent for approval. You can track its status in the transitions list.
              </Alert>
            </Grid>
          </Grid>
        );

      default:
        return null;
    }
  };

  // Show loading while fetching transition data
  if (loadingTransition) {
    return (
      <Box 
        sx={{ 
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(to bottom, #f5f7fa 0%, #ffffff 100%)'
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Loading transition data...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box 
      sx={{ 
        minHeight: '100vh',
        background: 'linear-gradient(to bottom, #f5f7fa 0%, #ffffff 100%)',
        pb: 4
      }}
    >
      <Box sx={{ maxWidth: 1400, mx: 'auto', px: 3, pt: 3 }}>
        {/* Header */}
        <Box 
          sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            mb: 4 
          }}
        >
          <Box>
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 700,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 0.5
              }}
            >
              {isEditMode ? 'Edit Employee Transition' : 'Create Employee Transition'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {isEditMode 
                ? 'Update transition details (only PENDING transitions can be edited)' 
                : 'Manage employee promotions, transfers, and salary increments'}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<BackIcon />}
            onClick={() => navigate('/userdashboard/staff/hrm/transitions')}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              px: 3,
              py: 1,
              borderColor: '#667eea',
              color: '#667eea',
              '&:hover': {
                borderColor: '#764ba2',
                bgcolor: 'rgba(102, 126, 234, 0.04)'
              }
            }}
          >
            Back to List
          </Button>
        </Box>

        {/* Stepper */}
        <Paper 
          elevation={0}
          sx={{ 
            p: 4, 
            mb: 3,
            borderRadius: 3,
            border: '1px solid #e0e0e0',
            background: 'white'
          }}
        >
          <Stepper 
            activeStep={activeStep} 
            alternativeLabel
            sx={{
              '& .MuiStepLabel-root .Mui-completed': {
                color: '#4caf50',
              },
              '& .MuiStepLabel-root .Mui-active': {
                color: '#667eea',
              },
              '& .MuiStepConnector-line': {
                borderTopWidth: 2,
              },
            }}
          >
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel 
                  sx={{
                    '& .MuiStepLabel-label': {
                      fontSize: '0.95rem',
                      fontWeight: 500,
                    }
                  }}
                >
                  {label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Paper>

        {/* Form Content */}
        <Paper 
          elevation={0}
          sx={{ 
            p: 4, 
            mb: 3,
            borderRadius: 3,
            border: '1px solid #e0e0e0',
            background: 'white',
            minHeight: 400
          }}
        >
          {renderStepContent()}
        </Paper>

        {/* Navigation Buttons */}
        <Box 
          sx={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            maxWidth: 900,
            mx: 'auto'
          }}
        >
          <Button
            variant="outlined"
            onClick={handleBack}
            disabled={activeStep === 0 || loading}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              px: 4,
              py: 1.5,
              fontSize: '1rem',
              fontWeight: 500,
              '&:disabled': {
                opacity: 0.5
              }
            }}
          >
            Back
          </Button>

          <Box sx={{ display: 'flex', gap: 2 }}>
            {activeStep < steps.length - 1 ? (
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={loading}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  px: 4,
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 500,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                  }
                }}
              >
                Next
              </Button>
            ) : (
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSubmit}
                disabled={loading}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  px: 4,
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 500,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                  }
                }}
              >
                {loading ? 'Submitting...' : 'Submit Transition Request'}
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default TransitionCreateForm;
